import { WindowElement, FloorPlanElement } from '../types';

/**
 * Generate the next sequential window ID (W1, W2, W3, etc.)
 */
export const generateNextWindowId = (currentCounter: number): string => {
  return `W${currentCounter}`;
};

/**
 * Find the highest existing window display ID and return the next counter value
 */
export const calculateNextWindowCounter = (elements: FloorPlanElement[]): number => {
  const windows = elements.filter((el): el is WindowElement => el.type === 'window');
  
  let maxCounter = 0;
  
  windows.forEach(window => {
    if (window.displayId) {
      // Extract number from W1, W2, etc.
      const match = window.displayId.match(/^W(\d+)$/);
      if (match) {
        const num = parseInt(match[1], 10);
        if (num > maxCounter) {
          maxCounter = num;
        }
      }
    }
  });
  
  return maxCounter + 1;
};

/**
 * Assign sequential display IDs to windows that don't have them
 * Returns updated elements array and the next counter value
 */
export const assignMissingWindowIds = (
  elements: FloorPlanElement[]
): { updatedElements: FloorPlanElement[]; nextCounter: number } => {
  const updatedElements = [...elements];
  let counter = calculateNextWindowCounter(elements);
  
  // Find windows without displayId and assign them
  for (let i = 0; i < updatedElements.length; i++) {
    const element = updatedElements[i];
    if (element.type === 'window' && !element.displayId) {
      (element as WindowElement).displayId = generateNextWindowId(counter);
      counter++;
    }
  }
  
  return {
    updatedElements,
    nextCounter: counter
  };
};

/**
 * Get all window display IDs for debugging/validation
 */
export const getWindowDisplayIds = (elements: FloorPlanElement[]): string[] => {
  return elements
    .filter((el): el is WindowElement => el.type === 'window')
    .map(window => window.displayId || 'NO_ID')
    .sort();
};
